/**
 * Makes sure one fan's baseline never produces another fan's toasts.
 *
 * `reconcileLoyaltyToasts` decides what is new by diffing against a snapshot in sessionStorage
 * (`urc.loyalty.toastSnapshot`), which survives navigation for the life of the tab. That is the
 * feature -- an award that landed while the tab was closed still toasts on the next load -- but it
 * means the snapshot outlives a sign-out. Sign out as one fan and in as another in the same tab and
 * the second fan is diffed against the first fan's tier and badges, which mints congratulations for
 * things they never did.
 *
 * The library exports `clearToastSnapshot()` for exactly this and says nothing about when to call
 * it, because only the host knows who is behind the browser. This module is that answer.
 *
 * It owns the feed mark in watermark.js for the same reason -- that mark is what lets a page SKIP a
 * reconcile, so leaving one fan's mark beside another fan's cleared snapshot would skip the reconcile
 * that was supposed to re-baseline, and the new fan's first award would vanish into it.
 *
 * **A missed toast beats a wrong toast.** Every ambiguous case here clears rather than keeps: the
 * cost of clearing is one silent baselining load, and the cost of keeping is telling somebody they
 * reached a tier they did not.
 *
 * WHAT REPLACED THE FINGERPRINT, AND WHY IT IS NOT A SMALLER VERSION OF IT
 * -----------------------------------------------------------------------
 * This used to build a fingerprint out of every `auth0_user_id` cookie in the jar, keep its own
 * record of it under `urc.loyalty.toastFan`, and clear the baseline whenever the two disagreed.
 * Three things were wrong with that and only one of them was the cookie:
 *
 * - **It read the jar two network round trips into the page.** `guardToastIdentity()` ran from
 *   `startToastTrigger()`, which is reached only after the whole gate chain settles `armed` -- so it
 *   read *after* the third-party script that expires every `auth0_*` cookie on `DOMContentLoaded`.
 *   An empty read is `current === ''`, which cleared **unconditionally**. The guard meant to protect
 *   a baseline was probably throwing it away on ordinary loads.
 * - **It had to filter the literal `'unknown'`**, which `auth0-login.php` wrote into that cookie
 *   whenever `/userinfo` came back unreadable -- so two different fans could present the same id.
 * - **It was a guard, so it had an ordering**, and a caller could run it late or not at all.
 *   `toasts/enrolled.js`'s docblock records what that cost: its own cache was read from
 *   `whenToastEligible()`, long before this ever ran.
 *
 * `window.urcAuth.onIdentityChange` is none of those. It compares the fan against the owner
 * recorded in this tab's `sessionStorage` rather than against whatever the page last saw, so it
 * catches a switch that happened in another document; it fires before anything repaints; and it
 * **replays for a late subscriber**, which is exactly what this module is -- so subscribing two
 * round trips into the page loses nothing.
 *
 * **It must not be reimplemented by reading `urc.auth.owner` here.** That record is written as part
 * of announcing the change, so a later read of it sees the NEW fan and can never detect a switch.
 * The signal is the subscription.
 */

import { clearToastSnapshot } from '@drewling/urc-loyalty-ui';
import { authState, onIdentityChange } from '../urcAuth';
import { clearWatermark } from './watermark';

/**
 * The library's own snapshot key. Read by `hasToastSnapshot()` for whether a baseline exists at all.
 */
const SNAPSHOT_KEY = 'urc.loyalty.toastSnapshot';

/**
 * @param {string} key Storage key.
 * @return {string|null} The stored value, or null when absent or storage is unavailable.
 */
function read( key ) {
	try {
		return window.sessionStorage.getItem( key );
	} catch ( error ) {
		return null;
	}
}

/**
 * Whether a baseline exists for `reconcileLoyaltyToasts` to diff against.
 *
 * A false answer means the next reconcile can only re-baseline and show nothing, so trigger.js must
 * run one now rather than wait for an arrival -- otherwise the arrival is what gets baselined
 * against, and it is swallowed.
 *
 * @return {boolean} Whether a snapshot is stored.
 */
export function hasToastSnapshot() {
	return read( SNAPSHOT_KEY ) !== null;
}

/**
 * Drop the baseline: the snapshot and the feed mark that says how far we have looked.
 *
 * Always both. A snapshot with no mark costs one silent baselining load; a mark with no snapshot
 * lets the next award be skipped as "nothing new" and then swallowed by the re-baseline.
 *
 * **The mark cleared is the NEW fan's**, which reads backwards and is right. The mark is keyed by
 * `sub` since 2026-09-07, so the previous fan's is a different key and cannot be read for anybody
 * else -- there is nothing to clear there. What has to go is any mark already sitting under the
 * fan who has just arrived, because the snapshot beside it is being dropped and a surviving mark
 * would let this tab skip the reconcile that was supposed to re-baseline them. It also clears the
 * pre-2026-09-07 unscoped key, which belonged to nobody in particular.
 *
 * @return {void}
 */
export function forgetToastBaseline() {
	const state = authState();

	clearToastSnapshot();
	clearWatermark( state && state.sub ? state.sub : '' );
}

/**
 * Drop the baseline whenever the fan behind this tab changes.
 *
 * Call once, when the toast layer arms. Covers a sign-out, one fan replacing another with no
 * sign-out in between, a switch performed in another tab, and a switch that happened while this
 * page sat in the back/forward cache -- the auth bundle re-resolves on `pageshow` and its cross-tab
 * channel carries the rest.
 *
 * @return {() => void} Unsubscribe.
 */
export function watchToastIdentity() {
	return onIdentityChange( forgetToastBaseline );
}
